/**
 * QuickAddNoteDialog — Inline NOTE/CALL create dialog (E-104)
 */

import type { ActivityItem } from '@/api/partners';
import { Button, Input } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreatePartnerNote } from '@/hooks/use-partner-activity';
import { useTranslation } from '@kgc/i18n';
import { Phone, StickyNote } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  locationId?: string;
  onCreated: (note: ActivityItem) => void;
}

export function QuickAddNoteDialog({
  open,
  onOpenChange,
  partnerId,
  locationId,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState<'NOTE' | 'CALL'>('NOTE');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const { create, isSubmitting, error, reset } = useCreatePartnerNote(partnerId);

  // Reset state each time the dialog opens
  useEffect(() => {
    if (open) {
      setType('NOTE');
      setTitle('');
      setDescription('');
      reset();
    }
  }, [open, reset]);

  const canSubmit = title.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const note = await create({
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        locationId,
      });
      onCreated(note);
    } catch {
      // error state is set by the hook — keep the dialog open
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('partner.timeline.quickAddDialog.title')}</DialogTitle>
          <DialogDescription>{t('partner.timeline.quickAddDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="grid grid-cols-2 gap-2"
            role="radiogroup"
            aria-label={t('partner.timeline.quickAddDialog.typeLabel')}
          >
            {(['NOTE', 'CALL'] as const).map(opt => {
              const Icon = opt === 'CALL' ? Phone : StickyNote;
              const isActive = type === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => setType(opt)}
                  className={`flex items-center justify-center gap-2 rounded-md border-2 px-4 py-3 text-sm font-semibold transition-all ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/30'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:bg-muted'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{t(`partner.timeline.types.TASK_${opt}`)}</span>
                </button>
              );
            })}
          </div>

          <div>
            <label htmlFor="note-title" className="mb-1 block text-sm font-medium">
              {t('partner.timeline.quickAddDialog.titleLabel')}
              <span className="ml-1 text-destructive" aria-hidden>
                *
              </span>
            </label>
            <Input
              id="note-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={255}
              placeholder={t('partner.timeline.quickAddDialog.titlePlaceholder')}
              autoFocus
              required
            />
          </div>

          <div>
            <label htmlFor="note-description" className="mb-1 block text-sm font-medium">
              {t('partner.timeline.quickAddDialog.descriptionLabel')}
            </label>
            <textarea
              id="note-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t('partner.timeline.quickAddDialog.descriptionPlaceholder')}
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isSubmitting ? t('common.saving') : t('partner.timeline.quickAddDialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
